import { useAuth } from './useAuth';

/**
 * A custom hook that returns an authenticated fetch function.
 * This function automatically adds the Authorization header and
 * handles 401 errors by attempting to refresh the token.
 */
export const useAuthenticatedApi = () => {
	const { token, refresh } = useAuth();

	const authenticatedFetch = async (url, options = {}, method = 'GET') => {
		// 1. Add the authorization header to the initial request
		const headers = {
			'Authorization': `Bearer ${token}`,
			...options.headers,
		};
	
		// Only add Content-Type if not dealing with FormData
		if (options.body && !(options.body instanceof FormData)) {
				headers['Content-Type'] = 'application/json';
				// Only stringify if it's not FormData
				if (options.body && typeof options.body === 'object') {
						options.body = JSON.stringify(options.body);
				}
		}
	
		const requestOptions = {
				...options,
				headers,
				method: method,
		};

		// 2. Make the initial API call
		let response = await fetch(url, requestOptions);
		// 3. Check if the response is a 401 Unauthorized
		if (response.status === 401) {
			console.log('Access token expired or invalid. Attempting to refresh...');
			try {
				// 4. Attempt to refresh the token
				const newToken = await refresh(); // refresh() is from AuthContext
				
				// 5. If refresh is successful, retry the original request with the new token
				const retryOptions = {
					...options,
					headers: {
						...headers,
						'Authorization': `Bearer ${newToken}`,
					},
					method: method
				};
				console.log('Token refreshed successfully. Retrying original request...');
				response = await fetch(url, retryOptions);

			} catch (error) {
				// 6. If refresh fails, the user is logged out by the refresh function in AuthContext.
				// We just need to re-throw the error to stop the current operation.
				console.error('Could not refresh token. User has been logged out.', error);
				throw new Error('Session expired. Please log in again.');
			}
		}

		// 7. Return the final response (either from the initial call or the retry)
		return response;
	};

	return { authenticatedFetch };
};
